import { LoginOutlined, ShareOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import { Button, Tooltip } from "@mui/material";
import React from "react";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { assetService } from "../../../Core/Api/services";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ModalContext } from "../../Modals";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import SelectModeBanner from "../SelectModeBanner";
import { IsDriveObject, IsObjectShareable } from "../../../Core/Utils/asset-object-utils";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import ModalCreateOrUpdateExternalAccess, {
  ModalCreateOrUpdateExternalAccessElementProps,
} from "../../Modals/ModalCreateOrUpdateExternalAccess/modal-create-or-update-external-access";
import CreateAccessButton from "../../Access/Shared/create-access-button";
import { useTranslation } from "react-i18next";

export interface CollectionSingleBannerProps {
  handleCloseClick: () => void;
  removePostAction: () => void;
  items: DbAssetObjectInfos[];
  collectionId: number;
}

const CollectionSingleBanner: React.FC<CollectionSingleBannerProps> = (props) => {
  const { t } = useTranslation();
  const { items, collectionId, handleCloseClick, removePostAction } = props;
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { addObjectByIdsToBasket } = useBasketActions();
  const enqueueSnackbar = useCustomSnackbar();

  const handleRemoveFromCollection = () => {
    assetService
      .DeleteFromCollection(
        collectionId,
        items.map((x) => x.id)
      )
      .then((data) => {
        if (!data.success) return;
        enqueueSnackbar(t("alert:success.objectsRemoved", { count: items.length }), "success");
        removePostAction();
        handleCloseClick();
      });
  };

  const handleAddToBasketClick = () => {
    addObjectByIdsToBasket(items.map((x) => x.id));
  };

  const openNewShareModal = () => {
    if (!openModal) return;
    openModal(ModalCreateShare, {
      assetIds: items.map((item) => item.id),
      isIndependentItems: true,
    });
  };

  const handleCreateExternalAccess = () => {
    if (!openModal) return;
    openModal<ModalCreateOrUpdateExternalAccessElementProps>(ModalCreateOrUpdateExternalAccess, {
      assets: items.filter((o) => o.classification === ObjectClass.Drive),
    });
  };

  return (
    <SelectModeBanner text={t("banner:body.itemSelected", { count: items.length })} onCloseClick={handleCloseClick}>
      <Button
        startIcon={<BookmarkBorderOutlinedIcon />}
        onClick={() => handleRemoveFromCollection()}
        variant="whiteSoft"
      >
        {t("button:removeFromCollection")}
      </Button>

      <Tooltip
        title={t("tooltip:shareClipsDisabled")}
        arrow
        disableHoverListener={!items.some((x) => !IsObjectShareable(x))}
      >
        <div>
          <Button
            disabled={items.some((x) => !IsObjectShareable(x))}
            onClick={openNewShareModal}
            variant="whiteSoft"
            startIcon={<ShareOutlined />}
          >
            {t("common:share")}
          </Button>
        </div>
      </Tooltip>

      <CreateAccessButton assets={items} variant="whiteSoft" disabled={items.some((x) => !IsDriveObject(x))} />

      <Button variant="white" startIcon={<AddIcon />} onClick={handleAddToBasketClick}>
        {t("common:addToBasket")}
      </Button>
    </SelectModeBanner>
  );
};

export default CollectionSingleBanner;
